import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { ArrowRightIcon } from '@heroicons/react/24/outline';

import { CardsCarouselSection } from '../../component/dashboard/cardCarousel';
import { CreatePostSection } from '../../component/dashboard/createPostSection';
import { DashboardControls } from '../../component/dashboard/dashboardControls';
import { ProfileStatsSection } from '../../component/dashboard/profileStats';
import {
  fetchProfile,
  getActiveDeals,
  getCardViewStats,
  getCardViewStatsById,
  getClosedDealPayments,
  getProfile,
  getProfileViewStats,
} from '../../services/api';
import {
  CardResponseDto,
  DealResponseDto,
  OpportunityCardView,
  PaymentResponseDto,
  Profile,
  ReadyRosteredCardView,
  TrackingResponseDto,
} from '../../types/types';

type SortOrder = 'oldest' | 'newest' | null;
type FilterType = 'most_viewed' | null;
type DealTab = 'active' | 'inactive';

const getDealCurrency = (
  deal: DealResponseDto,
  payment?: PaymentResponseDto
): string => {
  const candidates = [
    deal?.formData?.core?.currency,
    deal?.formData?.currency,
    deal?.formData?.details?.currency,
    deal?.formData?.payment?.currency,
    (deal as any)?.currency,
    (deal as any)?.paymentCurrency,
    (payment as any)?.currency,
    (payment as any)?.sentCurrency,
    (payment as any)?.receivedCurrency,
    (payment as any)?.dealCurrency,
  ];

  const found = candidates.find(
    (value) => typeof value === 'string' && value.trim() !== ''
  );

  return found || '—';
};

const getDealEventDate = (deal: DealResponseDto): string =>
  deal?.formData?.details?.eventDate ?? deal?.formData?.eventDate ?? '';

const formatAmount = (value: unknown): string => {
  const num = Number(value ?? 0);
  return Number.isFinite(num) ? num.toFixed(2) : '0.00';
};

const formatDate = (value?: string | null): string => {
  if (!value) return '—';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '—' : parsed.toLocaleDateString();
};

const formatCodeLabel = (value?: string | null): string =>
  String(value || '')
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .trim();

const cleanId = (value?: string | number | null): string => {
  if (value === null || value === undefined) return '';

  const text = String(value).trim();
  const smallText = text.toLowerCase();

  if (!text) return '';

  if (smallText === 'nan' || smallText === 'undefined' || smallText === 'null') {
    return '';
  }

  return text;
};

const tableHeaderStyle: React.CSSProperties = {
  padding: '10px 14px',
  textAlign: 'center',
  fontSize: '10px',
  lineHeight: 1.25,
  fontWeight: 600,
  color: '#6b7280',
  textTransform: 'uppercase',
  letterSpacing: '0.03em',
  whiteSpace: 'normal',
  overflowWrap: 'anywhere',
};

const leftHeaderStyle: React.CSSProperties = {
  ...tableHeaderStyle,
  textAlign: 'left',
};

const rightHeaderStyle: React.CSSProperties = {
  ...tableHeaderStyle,
  textAlign: 'right',
};

export const DashboardPage: React.FC = () => {
  const { t, i18n } = useTranslation('dashboard');
  const loginResponse = JSON.parse(localStorage.getItem('loginResponse') || '{}');
  const username = loginResponse.username || 'exampleUser';
  const loggedInUserName = loginResponse.username;
  const loggedInUserProfileType = loginResponse.profileType;

  const [totalIncome, setTotalIncome] = useState<number>(0);
  const [totalExpense, setTotalExpense] = useState<number>(0);
  const [totalDeals, setTotalDeals] = useState<number>(0);

  const navigate = useNavigate();

  const [profileMessage, setProfileMessage] = useState<string>(t('loading'));
  const [stats, setStats] = useState<TrackingResponseDto | null>(null);
  const [displayedProfileViews, setDisplayedProfileViews] = useState<number>(0);
  const [profileRange, setProfileRange] = useState<'7days' | 'month' | 'all'>('month');

  const [cardStats, setCardStats] = useState<CardResponseDto | null>(null);
  const [selectedCard, setSelectedCard] = useState<
    OpportunityCardView | ReadyRosteredCardView | null
  >(null);
  const [selectedChartType, setSelectedChartType] = useState<'profile' | 'card' | null>(
    'profile'
  );
  const [selectedCardStatsMap, setSelectedCardStatsMap] = useState<
    Record<string, TrackingResponseDto>
  >({});
  const [cardRangeMap, setCardRangeMap] = useState<
    Record<string, '7days' | 'month' | 'all'>
  >({});

  const [sortOrder, setSortOrder] = useState<SortOrder>(null);
  const [filterType, setFilterType] = useState<FilterType>(null);

  const [currentSlide, setCurrentSlide] = useState<number>(0);
  const [cardsPerSlide] = useState<number>(3);

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [payments, setPayments] = useState<PaymentResponseDto[]>([]);

  // ── Active / Inactive deal tab state ──────────────────────────────────────
  const [dealTab, setDealTab] = useState<DealTab>('inactive');
  const [activeDeals, setActiveDeals] = useState<DealResponseDto[]>([]);
  const [activeDealsLoading, setActiveDealsLoading] = useState<boolean>(false);
  // ──────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    const savedLanguage = localStorage.getItem('i18nextLng') || 'en';
    i18n.changeLanguage(savedLanguage);
  }, [i18n]);

  useEffect(() => {
    const fetchProfileStats = async () => {
      try {
        const statResult = await getProfileViewStats(loggedInUserName, profileRange);
        setStats({
          ...statResult,
          countryViews: statResult.countryViews || [],
        });

        let count = 0;
        const target = statResult.totalViews || 0;
        const step = Math.ceil(target / 30);

        const interval = setInterval(() => {
          count += step;
          if (count >= target) {
            count = target;
            clearInterval(interval);
          }
          setDisplayedProfileViews(count);
        }, 20);
      } catch (err) {
        setError(t('failed_to_load_profile_view_stats'));
      }
    };

    if (loggedInUserName) {
      fetchProfileStats();
    }
  }, [profileRange, loggedInUserName, t]);

  const fetchCardData = async () => {
    if (!loggedInUserName) return;

    try {
      let dateSort: string | undefined;
      let viewsSort: string | undefined;

      if (filterType === 'most_viewed') {
        viewsSort = 'desc';
      } else if (sortOrder !== null) {
        dateSort = sortOrder === 'oldest' ? 'asc' : 'desc';
      }

      const cardStatResult: any = await getCardViewStats(
        loggedInUserName,
        '7days',
        dateSort,
        viewsSort
      );

      const cards =
        (cardStatResult.markrtplaceCardViews && cardStatResult.markrtplaceCardViews.length > 0
          ? cardStatResult.markrtplaceCardViews
          : cardStatResult.readyRosteredCards) || [];

      const mappedResult: CardResponseDto = {
        totalViews: cardStatResult.totalViews || 0,
        cardViews: cards,
      };

      setCardStats(mappedResult);
    } catch (err) {
      toast.error(t('failed_to_load_card_view_stats'));
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!loggedInUserName) {
        setError(t('no_user_logged_in'));
        setProfileMessage(t('no_user_logged_in'));
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        const profileResponse = await fetchProfile();

        try {
          const message = await getProfile();
          setProfileMessage(message);
        } catch (err) {
          setProfileMessage(t('error_loading_profile'));
        }

        await fetchCardData();

        const paymentsResponse = await getClosedDealPayments();
        setPayments(paymentsResponse.paymentResponses || []);
        setTotalIncome(paymentsResponse.totalIncome || 0);
        setTotalExpense(paymentsResponse.totalExpense || 0);
        setTotalDeals(
          paymentsResponse.totalDeals || paymentsResponse.paymentResponses?.length || 0
        );
      } catch (error) {
        setError(t('failed_to_load_dashboard_data'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [loggedInUserName, loggedInUserProfileType, username, t]);

  useEffect(() => {
    if (loggedInUserName) {
      fetchCardData();
    }
  }, [sortOrder, filterType, loggedInUserName]);

  const handleDealTabChange = async (tab: DealTab) => {
    setDealTab(tab);
    if (tab === 'active' && activeDeals.length === 0) {
      try {
        setActiveDealsLoading(true);
        const response = await getActiveDeals();
        const deals = Array.isArray(response)
          ? response
          : response?.deals ?? response?.content ?? [];
        setActiveDeals(deals);
      } catch (err) {
        toast.error('Failed to load active deals');
      } finally {
        setActiveDealsLoading(false);
      }
    }
  };

  const handleSortClick = () => {
    let newSortOrder: SortOrder;
    if (sortOrder === null) {
      newSortOrder = 'oldest';
    } else if (sortOrder === 'oldest') {
      newSortOrder = 'newest';
    } else {
      newSortOrder = null;
    }
    setSortOrder(newSortOrder);
    if (newSortOrder !== null) {
      setFilterType(null);
    }
  };

  const handleMostViewedClick = () => {
    const newFilterType: FilterType = filterType === 'most_viewed' ? null : 'most_viewed';
    setFilterType(newFilterType);
    if (newFilterType === 'most_viewed') {
      setSortOrder(null);
    }
  };

  const handleCardSelect = async (
    card: OpportunityCardView | ReadyRosteredCardView
  ) => {
    setSelectedCard(card);
    setSelectedChartType('card');

    const cardId = 'cardId' in card ? card.cardId : card.id.toString();
    const cardType =
      loggedInUserProfileType === 'MANAGER' ? 'OPPORTUNITYCARD' : 'READYROSTERCARD';

    const alreadyHasStats = selectedCardStatsMap[cardId] !== undefined;
    const isAlreadySelected =
      selectedCard &&
      (('cardId' in selectedCard &&
        'cardId' in card &&
        selectedCard.cardId === card.cardId) ||
        ('id' in selectedCard && 'id' in card && selectedCard.id === card.id));

    if (!cardRangeMap[cardId]) {
      setCardRangeMap((prev) => ({ ...prev, [cardId]: '7days' }));
    }

    if (isAlreadySelected && alreadyHasStats) return;

    try {
      const stats = await getCardViewStatsById(
        loggedInUserName,
        cardId,
        cardType,
        '7days'
      );
      setSelectedCardStatsMap((prev) => ({ ...prev, [cardId]: stats }));
    } catch (err) {
      console.error('Error fetching card stats:', err);
    }
  };

  const handleCardRangeChange = async (
    card: OpportunityCardView | ReadyRosteredCardView,
    range: '7days' | 'month' | 'all'
  ) => {
    const cardId = 'cardId' in card ? card.cardId : card.id.toString();
    const cardType =
      loggedInUserProfileType === 'MANAGER' ? 'OPPORTUNITYCARD' : 'READYROSTERCARD';
    const rangeToSend = range === '7days' ? undefined : range;

    try {
      const stat = await getCardViewStatsById(
        loggedInUserName,
        cardId,
        cardType,
        rangeToSend
      );
      setSelectedCardStatsMap((prev) => ({ ...prev, [cardId]: stat }));
      setCardRangeMap((prev) => ({ ...prev, [cardId]: range }));
    } catch (err) {
      console.error('Failed to load selected card stats:', err);
    }
  };

  const totalSlides = cardStats?.cardViews
    ? Math.ceil(cardStats.cardViews.length / cardsPerSlide)
    : 0;

  const goToNextSlide = () =>
    setCurrentSlide((prev) => (totalSlides > 0 ? (prev + 1) % totalSlides : 0));

  const goToPrevSlide = () =>
    setCurrentSlide((prev) => (totalSlides > 0 ? (prev - 1 + totalSlides) % totalSlides : 0));

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center text-gray-600">{t('loading')}</div>
      </div>
    );
  }

  // ── Active Deals Table ────────────────────────────────────────────────────
  const getTranslatedText = (key: string, fallback: string): string => {
    const translated = t(key);
    return translated === key ? fallback : translated;
  };

  const getDealTypeLabel = (dealType?: string | null): string =>
    getTranslatedText(`deal_types.${dealType}`, formatCodeLabel(dealType));

  const getDealStatusLabel = (status?: string | null): string =>
    getTranslatedText(`deal_status.${status}`, formatCodeLabel(status));

  const dealPageId = (deal: DealResponseDto): string =>
    cleanId(deal.id) || cleanId(deal.dealId);

  const shownDealId = (deal: DealResponseDto): string =>
    cleanId(deal.dealId) || cleanId(deal.id) || '-';

  const openDealPage = (deal: DealResponseDto) => {
    const pageId = dealPageId(deal);

    if (!pageId) {
      toast.error('Deal ID not available');
      return;
    }

    navigate(`/deal/view/${pageId}`);
  };

  const ActiveDealsTable: React.FC = () => {
    if (activeDealsLoading) {
      return (
        <div className="flex items-center justify-center py-10 text-gray-500 text-sm">
          {t('loading_active_deals')}
        </div>
      );
    }

    if (activeDeals.length === 0) {
      return (
        <p className="text-gray-500 italic text-center py-8">
          {t('no_active_deals')}
        </p>
      );
    }

    return (
      <div className="w-full overflow-x-auto rounded-lg shadow-md border border-gray-200 bg-white">
        <table style={{ tableLayout: 'fixed', width: '100%', minWidth: '760px', borderCollapse: 'collapse' }}>
          <colgroup>
            <col style={{ width: '150px' }} />
            <col style={{ width: '130px' }} />
            <col style={{ width: '155px' }} />
            <col style={{ width: '170px' }} />
            <col style={{ width: '120px' }} />
          </colgroup>

          <thead style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
            <tr>
              <th style={tableHeaderStyle}>
                {t('deal_id')}
              </th>
              <th style={tableHeaderStyle}>
                {t('deal_type')}
              </th>
              <th style={tableHeaderStyle}>
                {t('status')}
              </th>
              <th style={leftHeaderStyle}>
                {t('user')}
              </th>
              <th style={rightHeaderStyle}>
                {t('created_at')}
              </th>
            </tr>
          </thead>

          <tbody>
            {activeDeals.map((deal, index) => {
              const counterparty = deal.initiatedBy || deal.acceptedBy;
              const pageId = dealPageId(deal);
              const displayId = shownDealId(deal);
              const fullName = counterparty
                ? `${counterparty.firstName} ${counterparty.lastName}`
                : '—';

              const getStatusColors = (status: string): React.CSSProperties => {
                const s = status?.toUpperCase() ?? '';
                if (s === 'PROPOSED')           return { background: '#dcfce7', color: '#15803d', border: '1px solid #bbf7d0' };
                if (s === 'ACCEPTED')           return { background: '#dbeafe', color: '#1d4ed8', border: '1px solid #bfdbfe' };
                if (s === 'CONFIRMED')          return { background: '#e0e7ff', color: '#4338ca', border: '1px solid #c7d2fe' };
                if (s === 'RESULT_CONFIRMED')   return { background: '#f3e8ff', color: '#7e22ce', border: '1px solid #e9d5ff' };
                if (s === 'RESULT_UPDATED')     return { background: '#fef9c3', color: '#a16207', border: '1px solid #fde68a' };
                if (s === 'WAITING_FOR_RESULT') return { background: '#ffedd5', color: '#c2410c', border: '1px solid #fed7aa' };
                if (s.includes('RATED'))        return { background: '#ccfbf1', color: '#0f766e', border: '1px solid #99f6e4' };
                return { background: '#f3f4f6', color: '#6b7280', border: '1px solid #e5e7eb' };
              };

              return (
                <tr
                  key={pageId || `active-deal-${index}`}
                  style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb', borderBottom: '1px solid #f3f4f6' }}
                >
                  <td style={{ padding: '12px 20px', fontSize: '13px', color: '#2563eb', fontWeight: 500, cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'center' }}>
                    <span
                      onClick={() => openDealPage(deal)}
                      onMouseEnter={e => (e.currentTarget.style.textDecoration = 'underline')}
                      onMouseLeave={e => (e.currentTarget.style.textDecoration = 'none')}
                    >
                      {displayId}
                    </span>
                  </td>

                  <td style={{ padding: '12px 20px', fontSize: '13px', color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'center' }}>
                    {getDealTypeLabel(deal.dealType)}
                  </td>

                  <td style={{ padding: '12px 20px', textAlign: 'center' }}>
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '2px 10px',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: 600,
                        letterSpacing: '0.03em',
                        whiteSpace: 'nowrap',
                        ...getStatusColors(deal.status ?? ''),
                      }}
                    >
                      {getDealStatusLabel(deal.status)}
                    </span>
                  </td>

                  <td style={{ padding: '12px 20px', fontSize: '13px', color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'left' }}>
                    {fullName}
                  </td>

                  <td style={{ padding: '12px 20px', fontSize: '13px', color: '#6b7280', whiteSpace: 'nowrap', textAlign: 'right' }}>
                    {formatDate(deal.createdAt)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="p-2 sm:p-4 font-sans">
        <div className="max-w-full mx-auto">
          <CreatePostSection />

          <DashboardControls
            sortOrder={sortOrder}
            filterType={filterType}
            currentSlide={currentSlide}
            totalSlides={totalSlides}
            onSortClick={handleSortClick}
            onMostViewedClick={handleMostViewedClick}
            onPrevSlide={goToPrevSlide}
            onNextSlide={goToNextSlide}
          />

          <div className="grid grid-cols-12 gap-4 lg:gap-6 mt-6">
            <div className="col-span-12 lg:col-span-4">
              <ProfileStatsSection
                stats={stats}
                displayedProfileViews={displayedProfileViews}
                profileRange={profileRange}
                onProfileRangeChange={setProfileRange}
                selectedChartType={selectedChartType}
                onChartTypeChange={(type) => {
                  setSelectedChartType(type);
                  if (type === 'profile') setSelectedCard(null);
                }}
                selectedCard={selectedCard}
                selectedCardStatsMap={selectedCardStatsMap}
                cardRangeMap={cardRangeMap}
              />
            </div>

            <div className="col-span-12 lg:col-span-8 space-y-8">
              <CardsCarouselSection
                cardStats={cardStats}
                selectedCard={selectedCard}
                onCardSelect={handleCardSelect}
                selectedCardStatsMap={selectedCardStatsMap}
                cardRangeMap={cardRangeMap}
                onCardRangeChange={handleCardRangeChange}
                currentSlide={currentSlide}
                setCurrentSlide={setCurrentSlide}
                cardsPerSlide={cardsPerSlide}
              />

              <section>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex gap-1 border border-slate-200 rounded-md p-0.5 bg-white shadow-sm">
                    <button
                      onClick={() => handleDealTabChange('active')}
                      className={`px-4 py-1.5 text-sm font-medium rounded transition-colors duration-150 ${
                        dealTab === 'active'
                          ? 'bg-slate-800 text-white shadow'
                          : 'text-slate-600 hover:text-slate-800'
                      }`}
                    >
                      {t('active_deal')}
                    </button>
                    <button
                      onClick={() => handleDealTabChange('inactive')}
                      className={`px-4 py-1.5 text-sm font-medium rounded transition-colors duration-150 ${
                        dealTab === 'inactive'
                          ? 'bg-slate-800 text-white shadow'
                          : 'text-slate-600 hover:text-slate-800'
                      }`}
                    >
                      {t('closed_deal')}
                    </button>
                  </div>

                  <button
                    onClick={() => navigate('/mydeals')}
                    className="flex items-center gap-1 text-sm font-bold text-slate-800 hover:text-blue-500"
                  >
                    {t('go_to_deals')}
                    <ArrowRightIcon className="w-4 h-4" />
                  </button>
                </div>

                {dealTab === 'inactive' && (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                      <div className="h-[74px] bg-white p-2 text-center shadow border border-slate-200 rounded-md flex flex-col justify-center">
                      <div className="text-[9px] mb-1 text-slate-500 font-medium">
                        {t('total_expense')}
                      </div>
                      <div className="text-lg font-bold text-slate-800 leading-none">
                        {formatAmount(totalExpense)}
                      </div>
                    </div>

                    <div className="h-[74px] bg-white p-2 text-center shadow border border-slate-200 rounded-md flex flex-col justify-center">
                      <div className="text-[9px] mb-1 text-slate-500 font-medium">
                        {t('total_deals')}
                      </div>
                      <div className="text-lg font-bold text-slate-800 leading-none">
                        {totalDeals}
                      </div>
                    </div>
                  </div>
                )}

                {dealTab === 'active' ? (
                  <ActiveDealsTable />
                ) : payments.length > 0 ? (
                  <div className="w-full overflow-x-auto rounded-lg shadow-md border border-gray-200 bg-white">
                    <table style={{ tableLayout: 'fixed', width: '100%', minWidth: '1080px', borderCollapse: 'collapse' }}>
                      <colgroup>
                        <col style={{ width: '170px' }} />
                        <col style={{ width: '150px' }} />
                        <col style={{ width: '170px' }} />
                        <col style={{ width: '170px' }} />
                        <col style={{ width: '150px' }} />
                        <col style={{ width: '120px' }} />
                        <col style={{ width: '150px' }} />
                      </colgroup>

                      <thead style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                        <tr>
                          <th style={tableHeaderStyle}>
                            {t('deal_id')}
                          </th>
                          <th style={tableHeaderStyle}>
                            {t('deal_type')}
                          </th>
                          <th style={tableHeaderStyle}>
                            {t('event_date')}
                          </th>
                          <th style={tableHeaderStyle}>
                            {t('expense_amount')}
                          </th>
                          <th style={tableHeaderStyle}>
                            {t('refund')}
                          </th>
                          <th style={tableHeaderStyle}>
                            {t('currency')}
                          </th>
                          <th style={tableHeaderStyle}>
                            {t('paid_at')}
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {payments.map((payment, index) => {
                          const deal = payment.deal;
                          const pageId = dealPageId(deal);
                          const displayId = shownDealId(deal);
                          const currency = getDealCurrency(deal, payment);
                          const eventDateStr = getDealEventDate(deal);

                          return (
                            <tr
                              key={pageId || `closed-deal-${index}`}
                              style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb', borderBottom: '1px solid #f3f4f6' }}
                            >
                              <td style={{ padding: '12px 20px', fontSize: '13px', color: '#2563eb', fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                <span
                                  onClick={() => openDealPage(deal)}
                                  onMouseEnter={e => (e.currentTarget.style.textDecoration = 'underline')}
                                  onMouseLeave={e => (e.currentTarget.style.textDecoration = 'none')}
                                >
                                  {displayId}
                                </span>
                              </td>

                              <td style={{ padding: '12px 20px', fontSize: '13px', color: '#374151', whiteSpace: 'nowrap', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {getDealTypeLabel(deal.dealType)}
                              </td>

                              <td style={{ padding: '12px 20px', fontSize: '13px', color: '#374151', whiteSpace: 'nowrap', textAlign: 'center' }}>
                                {formatDate(eventDateStr)}
                              </td>

                              <td style={{ padding: '12px 20px', fontSize: '13px', fontWeight: 600, color: '#ea580c', whiteSpace: 'nowrap', textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
                                {formatAmount(payment.sentAmount)}
                              </td>

                              <td style={{ padding: '12px 20px', fontSize: '13px', fontWeight: 600, color: '#16a34a', whiteSpace: 'nowrap', textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
                                {formatAmount(payment.receivedAmount)}
                              </td>

                              <td style={{ padding: '12px 20px', fontSize: '13px', color: '#374151', whiteSpace: 'nowrap', textAlign: 'center', fontWeight: 500 }}>
                                {currency}
                              </td>

                              <td style={{ padding: '12px 20px', fontSize: '13px', color: '#6b7280', whiteSpace: 'nowrap', textAlign: 'center' }}>
                                {formatDate(payment.paidAt)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-gray-500 italic text-center py-8">
                    {t('no_payments')}
                  </p>
                )}
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
